import { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Paper,
  tableCellClasses,
  Tooltip,
  Button,
  TextField,
  IconButton,
  Grid,
  Typography,
} from "@mui/material";
import { PlusOne, Delete, Edit, ContentCopy } from "@mui/icons-material";
import { formatPrice } from "utils/priceformatter";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 13,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    border: 0,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  backgroundColor: "white",
  "&:hover": {
    backgroundColor: "#DDDDDD !important",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:nth-child(2n)": {
    backgroundColor: "#fbfbfb",
  },
}));

export default function ArticlesTable({
  rows,
  editable = false,
  setLines = null,
}) {
  const [addItem, setAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    ItemNo: "",
    Description: "",
    Quantity: "",
    UnitOfMeasure: "",
    UnitPrice: "",
    LineAmountExclVAT: "",
    VATPct: "",
  });
  const [items, setItems] = useState(rows);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const newVal = value.replace(",", ".");
    setNewItem((prevData) => ({ ...prevData, [name]: newVal }));
  };

  const handleAddItem = () => {
    setAddItem(false);
    const tmpNewItem = {
      ...newItem,
      LineAmountExclVAT: getLineAmountExclVAT(newItem),
    };
    const updatedRows = [...items, tmpNewItem];
    setItems(updatedRows);
    setLines(updatedRows);
    setNewItem({
      ItemNo: "",
      Description: "",
      Quantity: "",
      UnitOfMeasure: "",
      UnitPrice: "",
      LineAmountExclVAT: "",
      VATPct: "",
    });
  };

  const handleDuplicate = (index) => {
    const newItem = items[index];
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    setLines(updatedItems);
  };

  const handleRemoveItem = (index) => {
    const updatedItems = [...items];
    updatedItems.splice(index, 1);

    setItems(updatedItems);
    setLines(updatedItems);
  };

  const handleEditItem = (index) => {
    setNewItem(items[index]);
    setAddItem(true);
    handleRemoveItem(index);
  };

  const getLineAmountExclVAT = (item) => {
    return Number(item.Quantity || 0) * Number(item.UnitPrice || 0);
  };

  const totExclVAT = useMemo(() => {
    const number = items.reduce((partialSum, item) => {
      return Number(partialSum) + Number(getLineAmountExclVAT(item) || 0);
    }, 0);
    return number;
  }, [items]);

  const totVAT = useMemo(() => {
    return items.reduce(
      (partialSum, item) =>
        partialSum +
        (Number(getLineAmountExclVAT(item) || 0) * Number(item.VATPct || 0)) /
          100,
      0
    );
  }, [items]);

  const totInclVAT = useMemo(() => {
    return items.reduce(
      (partialSum, item) =>
        partialSum +
        Number(getLineAmountExclVAT(item) || 0) *
          (Number(item.VATPct || 0) / 100 + 1),
      0
    );
  }, [items]);

  return (
    <TableContainer
      component={Paper}
      sx={{ mt: 7, borderRadius: "0.25rem", boxShadow: "0" }}
    >
      <Table
        sx={{ overflow: "hidden", width: "100%" }}
        aria-label="articles table"
      >
        <TableHead>
          <TableRow>
            <StyledTableCell>Artikelnummer</StyledTableCell>
            <StyledTableCell align="left">Beskrivning</StyledTableCell>
            <StyledTableCell align="right">Antal</StyledTableCell>
            <StyledTableCell align="right">Enhet</StyledTableCell>
            <StyledTableCell align="right">A-Pris</StyledTableCell>
            <StyledTableCell align="right">Radbelopp</StyledTableCell>
            <StyledTableCell align="right">% Moms</StyledTableCell>
            {editable && <StyledTableCell align="right" />}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, index) => (
            <StyledTableRow key={index}>
              <StyledTableCell component="th" scope="item">
                {item.ItemNo}
              </StyledTableCell>
              <StyledTableCell align="left">{item.Description}</StyledTableCell>
              <StyledTableCell align="right">
                {formatPrice(item.Quantity)}
              </StyledTableCell>
              <StyledTableCell align="right">
                {item.UnitOfMeasure}
              </StyledTableCell>
              <StyledTableCell align="right">
                {formatPrice(item.UnitPrice)}
              </StyledTableCell>
              <StyledTableCell align="right">
                {formatPrice(item.LineAmountExclVAT)}
              </StyledTableCell>
              <StyledTableCell align="right">
                {formatPrice(item.VATPct)}
              </StyledTableCell>
              {editable && (
                <StyledTableCell
                  style={{ display: "flex", flexDirection: "row" }}
                >
                  <>
                    <IconButton onClick={() => handleEditItem(index)}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDuplicate(index)}>
                      <ContentCopy />
                    </IconButton>
                    <IconButton onClick={() => handleRemoveItem(index)}>
                      <Delete />
                    </IconButton>
                  </>
                </StyledTableCell>
              )}
            </StyledTableRow>
          ))}
          {editable && addItem && (
            <StyledTableRow
              style={{
                padding: "10px",
                width: "100%",
              }}
            >
              <StyledTableCell>
                <TableCellInput
                  handleInputChange={handleInputChange}
                  value={newItem.ItemNo}
                  editable
                  name={"ItemNo"}
                  placeholder={"Artikelnummer"}
                />
              </StyledTableCell>
              <StyledTableCell>
                <TableCellInput
                  handleInputChange={handleInputChange}
                  value={newItem.Description}
                  editable
                  name={"Description"}
                  placeholder={"Beskrivning"}
                />
              </StyledTableCell>
              <StyledTableCell align="right">
                <TableCellInput
                  handleInputChange={handleInputChange}
                  value={newItem.Quantity}
                  editable
                  name={"Quantity"}
                  placeholder={"Antal"}
                />
              </StyledTableCell>
              <StyledTableCell align="right">
                <TableCellInput
                  handleInputChange={handleInputChange}
                  value={newItem.UnitOfMeasure}
                  editable
                  name={"UnitOfMeasure"}
                  placeholder={"Enhet"}
                />
              </StyledTableCell>
              <StyledTableCell align="right">
                <TableCellInput
                  handleInputChange={handleInputChange}
                  value={newItem.UnitPrice}
                  editable
                  name={"UnitPrice"}
                  placeholder={"A-pris"}
                />
              </StyledTableCell>
              <StyledTableCell align="right">
                <TableCellInput
                  handleInputChange={handleInputChange}
                  value={formatPrice(getLineAmountExclVAT(newItem))}
                  name={"LineAmountExclVAT"}
                  placeholder={"Radbelopp"}
                />
              </StyledTableCell>
              <StyledTableCell align="right">
                <TableCellInput
                  handleInputChange={handleInputChange}
                  value={newItem.VATPct}
                  editable
                  name={"VATPct"}
                  placeholder={"Moms %"}
                />
              </StyledTableCell>
              <StyledTableCell align="right" />
            </StyledTableRow>
          )}
        </TableBody>
      </Table>
      {editable && (
        <Grid item container alignItems={"center"}>
          <Grid item md={4} />
          <Grid item container md={4} justifyContent="center" my={1}>
            <Button
              variant="outlined"
              startIcon={addItem ? null : <PlusOne />}
              onClick={() => setAddItem(!addItem)}
            >
              {addItem ? "Avbryt" : "Lägg till"}
            </Button>
            {addItem && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleAddItem}
                sx={{ margin: "0 8px" }}
              >
                Bekräfta
              </Button>
            )}
          </Grid>
          <Grid item container md={4} direction={"column"} padding={"10px"}>
            <Grid
              item
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2">{`Summa exkl moms:`}</Typography>
              <Typography variant="body2">{`${formatPrice(
                totExclVAT
              )} kr`}</Typography>
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2">{`Moms:`}</Typography>
              <Typography variant="body2">{`${formatPrice(
                totVAT
              )} kr`}</Typography>
            </Grid>
            <Grid
              item
              sx={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="body2">{`Att betala:`}</Typography>
              <Typography variant="body2">{`${formatPrice(
                totInclVAT
              )} kr`}</Typography>
            </Grid>
          </Grid>
        </Grid>
      )}
    </TableContainer>
  );
}

const TableCellInput = ({
  value,
  editable,
  name,
  placeholder,
  handleInputChange,
}) => {
  return editable ? (
    <TextField
      padding={0}
      width={"100%"}
      height={"100%"}
      value={value}
      name={name}
      onChange={handleInputChange}
      size="small"
      fullWidth
      margin="dense"
      variant="standard"
      style={{ backgroundColor: "white" }}
      placeholder={placeholder}
    />
  ) : (
    <span>{value}</span>
  );
};
